/* eslint-disable react-hooks/exhaustive-deps */
import { SearchIcon } from '@chakra-ui/icons';
import { Box, Button, Center, Divider, HStack, Text } from '@chakra-ui/react';
import { CQTreeTable, TextInput } from '@laxmimanogna/code-quick-components';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppColors from '../../../constants/AppColors';
import ROUTES from '../../../constants/Routes';
import { FONT_FAMILY } from '../../../constants/Theme';
import { AuthContext } from '../../../providers/AuthProvider';
import { DashboardContext } from '../../../providers/DashboardProvider';
import { replaceRoute, toTitleCase } from '../../../utils/common.utils';
import { getCurrentUserTypeIdObject } from '../../../utils/getCurrentUserTypeDetails';
import { defaultTreeTableProp } from './table_config/cq_tree_table_props';
import { priceConverter } from '../../../utils/priceConverter';
import InfoTooltip from '../../../components/InfoTooltip';

const columnHelper = createColumnHelper();

const NA = () => <Text color={AppColors.gray} textAlign="center">N/A</Text>;

// PhysicianTable is a flat (no tree) provider-only table.
// entity_type=department, so the API returns providers as the top-level children.
// No subRows — CQTreeTable renders them as a simple flat table with no expand carets.
function PhysicianTable({departmentId=null}) {
  const tableRef = useRef(null);
  const { hospitalId } = useParams();
  const dashboardContext = useContext(DashboardContext);
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  const { treeData, isHierarchyLoading, getHierarchyTree, hospitalFilter, setHospitalFilter, filterParams } = dashboardContext;
  const [searchText, setSearchText] = useState('');
  const [ordering, setOrdering]     = useState('');

  useEffect(() => {
    const obj = getCurrentUserTypeIdObject(authContext.currentUser);
    setHospitalFilter(prev => ({ ...prev, ...obj }));
  }, [authContext.currentUser]);

  useEffect(() => {
    if (hospitalFilter.department_id || departmentId) {
      getHierarchyTree('department', hospitalFilter.department_id || departmentId, {
        search: hospitalFilter?.search || '',
        ordering,
      });
    }
  }, [hospitalFilter, ordering, departmentId]);

  const onFilterChange = e => setSearchText(e ? e.target.value : '');
  const onSearchAction = () => setHospitalFilter(prev => ({ ...prev, search: searchText }));

  // All rows are providers — depth 0 only, no subRows, no expand carets
  const columns = useMemo(() => [

    columnHelper.accessor('name', {
      id: 'name',
      header: () => 'Provider Name',
      cell: info => {
        const { first_name, last_name } = info.row.original;
        const display = info.getValue() || toTitleCase(`${first_name ?? ''} ${last_name ?? ''}`.trim());
        return <Text fontSize="14px" textAlign={"start"} color={AppColors.secondary}>{display}</Text>;
      },
    }),

    columnHelper.accessor('total_uploads', {
      id: 'total_uploads',
      header: () => 'Uploads',
      cell: info => {
        const val = info.getValue();
        return val != null ? <Text textAlign="center" fontSize="13px">{val}</Text> : <NA />;
      },
    }),

    columnHelper.accessor('chart_accuracy', {
      id: 'chart_accuracy',
      header: () => 'Chart Accuracy',
      cell: info => {
        const val = info.getValue();
        return val != null ? <Text textAlign="center" fontSize="13px">{val}%</Text> : <NA />;
      },
    }),

    columnHelper.accessor('cq_rev_opp', {
      id: 'cq_rev_opp',
      header: () => 'RI Rev. Opp',
      cell: info => {
        const val = info.getValue();
        return val != null ? <Text textAlign="center" fontSize="13px">{priceConverter(val)}</Text> : <NA />;
      },
    }),

    columnHelper.accessor('organization_rank', {
      id: 'organization_rank',
      header: () => 'Organization Rank',
      cell: info => {
        const val = info.getValue();
        return val != null ? <Text textAlign="center" fontSize="13px">{val}</Text> : <NA />;
      },
    }),

    columnHelper.accessor(row => row.id, {
      id: 'action',
      header: () => 'Action',
      enableSorting: false,
      cell: info => {
        const row = info.row.original;
        return (
          <Button
            size="sm"
            bg={AppColors.secondary}
            color="white"
            borderRadius="3xl"
            px={4}
            fontWeight="normal"
            fontSize="sm"
            _hover={{ transition: 'all .1s ease', bgColor: AppColors.white,
              color: AppColors.secondary, outline: `2px solid ${AppColors.secondary}` }}
            onClick={() =>
              navigate(
                replaceRoute(
                  hospitalFilter.health_system_id
                    ? ROUTES.PHYSICIAN_DASHBOARD
                    : hospitalFilter.hospital_id
                    ? ROUTES.HOSPITAL_PHYSICIAN_DASHBOARD
                    : ROUTES.DEPARTMENT_PHYSICIAN_DASHBOARD,
                  {
                    hospitalId: row.hospital_id ?? hospitalId,
                    departmentId: row.department_id,
                    physicianId: row.id,
                  }
                )
              )
            }
          >
            View
          </Button>
        );
      },
    }),

  ], [hospitalFilter, filterParams, navigate]);

  return (
    <Box marginTop={3} width="100%" bg={AppColors.white} padding={4} borderRadius="lg" shadow="md">
      <HStack width="100%" justifyContent="space-between" mb={3}>
        <HStack spacing={2}>
          <Text fontFamily={FONT_FAMILY.ptSans} color={AppColors.primary}>Providers</Text>
          <InfoTooltip infoContent={
            <p><b>Providers</b> — each provider in this department. Click on a Provider to view further details, providing a comprehensive overview of each facility's performance and metrics.</p>
          } />
        </HStack>
        <Box>
          <TextInput
            borderRadius="md"
            onKeyDown={e => { if (e.keyCode === 13) onSearchAction(); }}
            onChange={e => onFilterChange(e)}
            rightIconProps={{ zIndex: 0 }}
            placeholder="Search Providers"
            rightIcon={<SearchIcon color={AppColors.gray} />}
          />
        </Box>
      </HStack>
      <Divider />
      <Box
        id="fixedTableHeader"
        maxHeight={'400px'}
        overflowY={'scroll'}
        className="tableWraper"
      >
      <CQTreeTable
        ref={tableRef}
        data={treeData ?? []}
        columns={columns}
        // No subRows — providers are leaf nodes
        size={9999}
        getSubRows={() => undefined}
        isLoading={isHierarchyLoading}
        manualSortBy={false}
        {...defaultTreeTableProp}
        onAsc={h  => setOrdering(h)}
        onDesc={h => setOrdering(`-${h}`)}
      />
      </Box>

      {!isHierarchyLoading && !treeData?.length && (
        <Center mt={10} fontFamily={FONT_FAMILY.ptSans}>No data available</Center>
      )}
    </Box>
  );
}

export default PhysicianTable;